/**
 * useWebSocketChat — real-time chat hook with streaming support.
 *
 * Features:
 *  - JWT-authenticated WebSocket connection
 *  - Auto-reconnect with exponential backoff
 *  - Token-by-token streaming from the LLM
 *  - Typing indicators
 *  - Falls back to REST if WS unavailable
 *
 * Usage:
 *  const { messages, connected, streaming, sendMessage, clearSession }
 *    = useWebSocketChat();
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { getCsrfToken, isAuthenticated, tryRefreshToken } from "../api/client";
import { createLogger } from "../utils/logger";
const log = createLogger("WS");

const WS_BASE = (window.location.protocol === "https:" ? "wss://" : "ws://") + window.location.host;

// A4 (#4): mint a short-lived single-use ticket so the JWT never rides in the WS
// URL (URLs leak into logs / history / proxies). Same-origin API (nginx proxies
// /api). Falls back to the token on any failure (Redis down / old server) so WS
// stays available.
export async function wsChatUrl() {
  // F (#16): mint the single-use ticket over a cookie-authed POST (credentials +
  // CSRF header) — no JWT in JS or in the URL. On any failure the browser still
  // sends the HttpOnly i2_access cookie on the same-origin WS handshake, so we
  // connect with no query param and the server authenticates off the cookie.
  try {
    const csrf = getCsrfToken();
    const res = await fetch("/api/auth/ws-ticket", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
    });
    if (res.ok) {
      const data = await res.json();
      const ticket = data && data.data && data.data.ticket;
      if (ticket) return `${WS_BASE}/ws/chat?ticket=${encodeURIComponent(ticket)}`;
    }
  } catch { /* fall through to the cookie-handshake connection */ }
  return `${WS_BASE}/ws/chat`;
}
const MAX_RECONNECT_DELAY = 30000;
const MAX_RECONNECT_ATTEMPTS = 20;
const HEARTBEAT_INTERVAL = 25000; // 25s ping to detect dead connections

// Number of messages to persist per chat surface — keeps localStorage
// from growing unbounded on long conversations.
const PERSIST_MESSAGE_CAP = 200;

/**
 * @param {object}  opts
 * @param {string?} opts.storageKey — when set, the messages array is
 *   hydrated from localStorage on mount and persisted on every change.
 *   `clearSession` wipes the entry. Pass `null`/undefined to opt out
 *   (transient session, default).
 */
export function useWebSocketChat({ storageKey } = {}) {
  // Hydrate messages from localStorage if a storage key is provided.
  // Per-agent / per-mode chats can persist across panel close + reopen
  // (users complained "we lose the context and have to start from
  // scratch" when the chat panel was closed). The hydrated entries
  // are display-only — server-side conversation_id is not preserved
  // here, so a fresh send opens a new server thread but the visible
  // message history stays intact.
  const _initial = (() => {
    if (!storageKey) return [];
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return [];
      // Strip any in-flight streaming flags from the persisted state —
      // those would render a spinner that never resolves. Also drop
      // the hidden "[System context: ...]" priming user message in
      // case a previous build wrote it to storage; otherwise users
      // with stale state would still see the blank-panel symptom on
      // reopen.
      return arr
        .filter(m => !(m.role === "user" && (m.content || "").startsWith("[System context:")))
        .map(m => ({ ...m, streaming: false }))
        .slice(-PERSIST_MESSAGE_CAP);
    } catch { return []; }
  })();
  const [messages, setMessages] = useState(_initial);

  // Persist messages whenever they change. Capped + JSON-stringified;
  // failures (quota exceeded / private-browsing) silently drop the
  // write since chat must keep working even if storage isn't.
  //
  // Skip the hidden "[System context: ...]" priming message that
  // AgentBuilderChat sends to the Super Agent on first open. It's
  // filtered from display anyway, but persisting it caused a subtle
  // bug: if the user closed the panel before the Super Agent replied,
  // the next open hydrated `[{role:"user", content:"[System context:..."}]`
  // — the welcome guard saw messages.length === 1 and skipped showing
  // "Connecting to Agent Builder...", and the auto-context useEffect
  // also skipped re-sending. The user got a blank panel.
  useEffect(() => {
    if (!storageKey) return;
    try {
      const persistable = messages.filter(m => !(m.role === "user" && (m.content || "").startsWith("[System context:")));
      const slim = persistable.slice(-PERSIST_MESSAGE_CAP).map(m => ({
        // Trim large fields that don't need to round-trip
        role: m.role,
        content: m.content,
        status: m.status,
        error: m.error,
        timestamp: m.timestamp,
        fileIds: m.fileIds,
        citations: m.citations,
        richElements: m.richElements,
        verification: m.verification,
        conversationId: m.conversationId,
      }));
      localStorage.setItem(storageKey, JSON.stringify(slim));
    } catch {}
  }, [messages, storageKey]);

  const [connected, setConnected] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [agentInfo, setAgentInfo] = useState(null);
  const [agentTyping, setAgentTyping] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);

  const wsRef = useRef(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef(null);
  const heartbeatTimer = useRef(null);
  const streamBuffer = useRef("");
  const streamMsgId = useRef(null);
  const intentionalClose = useRef(false);
  // Token queue — tokens land here from onmessage; a setInterval drains them
  // one-per-tick so React renders each token individually even if the WS
  // delivers all frames in a single burst (common on localhost / SSL).
  const tokenQueue = useRef([]);
  const streamInterval = useRef(null);
  const streamEnded = useRef(false);   // set by stream_end, checked by interval
  const TOKEN_DRAIN_MS = 28; // ~35 tokens/sec — smooth but not sluggish

  // ─── Connect ─────────────────────────────────────────

  const _startHeartbeat = useCallback(() => {
    clearInterval(heartbeatTimer.current);
    heartbeatTimer.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        } catch {
          // Connection dead — trigger reconnect
          wsRef.current.close();
        }
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  const _stopHeartbeat = useCallback(() => {
    clearInterval(heartbeatTimer.current);
  }, []);

  // Tracks consecutive auth failures so we don't loop forever refreshing
  // a refresh token that has itself died (>30d idle, or revoked). Reset
  // on every successful onopen.
  const authRetryCount = useRef(0);
  const MAX_AUTH_RETRIES = 1;

  const connect = useCallback(async () => {
    // F (#16): the JWT is in an HttpOnly cookie the JS can't read, so we can't
    // decode its exp for a proactive refresh. Gate on the readable session
    // signal (the CSRF cookie) instead; token expiry mid-session is handled
    // reactively — the server closes 4001, and the close handler below refreshes
    // (cookie-based) and reconnects.
    if (!isAuthenticated()) return;

    // Clean up existing
    intentionalClose.current = false;
    if (wsRef.current) {
      intentionalClose.current = true;
      wsRef.current.close();
      intentionalClose.current = false;
    }

    const ws = new WebSocket(await wsChatUrl());  // A4 ticket / F cookie-handshake
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      setReconnecting(false);
      reconnectAttempt.current = 0;
      authRetryCount.current = 0;
      _startHeartbeat();
      log.info("Connected");
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "pong") return; // heartbeat response
        // Server-driven re-auth: when the token expires mid-session the
        // backend sends `{type: "token_expired"}` then closes 4001. We
        // don't have to do anything special here — the close handler
        // refreshes and reconnects. Surfacing the message is enough so
        // future UI hooks (a "session expired" toast) can listen.
        if (data.type === "token_expired") {
          log.info("Server signalled token_expired; refresh+reconnect on close");
          return;
        }
        _handleMessage(data);
      } catch (e) {
        log.error("Parse error:", e);
      }
    };

    ws.onclose = (event) => {
      setConnected(false);
      _stopHeartbeat();
      wsRef.current = null;
      log.info(`Closed: code=${event.code} reason=${event.reason}`);

      if (intentionalClose.current) return;

      // Industry-standard close codes (RFC 6455 §7.4 application range).
      // 4003 = Forbidden — terminal, do not retry. The token is valid
      //        but lacks permission for the resource; refreshing won't
      //        change that.
      if (event.code === 4003) {
        log.error("WS forbidden (4003); not reconnecting");
        setReconnecting(false);
        return;
      }

      // 4001 = Unauthorized — token missing/invalid/expired. Refresh
      //        and try ONCE. If we hit 4001 again, the refresh token
      //        is dead too and the user needs to log in.
      if (event.code === 4001) {
        if (authRetryCount.current >= MAX_AUTH_RETRIES) {
          log.error("WS unauthorized after refresh; giving up");
          setReconnecting(false);
          return;
        }
        authRetryCount.current++;
        log.info("WS unauthorized; refreshing token and reconnecting");
        // Fire and forget — connect() re-checks the cookie session itself,
        // and tryRefreshToken happens at the top of connect when it
        // detects the stale token. We trigger immediately rather than
        // applying exponential backoff because the refresh is the
        // failure recovery, not a reconnect retry.
        tryRefreshToken().then((ok) => {
          if (ok) connect();
          else {
            log.error("Refresh failed after 4001; user must log in");
            setReconnecting(false);
          }
        });
        return;
      }

      // Max attempts reached
      if (reconnectAttempt.current >= MAX_RECONNECT_ATTEMPTS) {
        log.error(`Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached — giving up`);
        setReconnecting(false);
        return;
      }

      // Exponential backoff with jitter — generic transient failure
      // (network blip, server restart, idle timeout).
      const base = Math.min(1000 * Math.pow(2, reconnectAttempt.current), MAX_RECONNECT_DELAY);
      const jitter = Math.random() * 1000; // 0-1s random jitter to prevent thundering herd
      const delay = base + jitter;
      reconnectAttempt.current++;
      setReconnecting(true);
      log.info(`Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttempt.current}/${MAX_RECONNECT_ATTEMPTS})`);
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = (err) => {
      log.error("Error:", err);
    };
  }, [_startHeartbeat, _stopHeartbeat]);

  // ─── Handle Incoming Messages ────────────────────────

  const _handleMessage = useCallback((data) => {
    switch (data.type) {
      case "connected":
        log.debug("Authenticated:", data);
        break;

      case "ack":
        // Message received by server — assign messageId and mark delivered
        setMessages((prev) =>
          prev.map((m) =>
            m.role === "user" && m.status === "sent" && !m.messageId
              ? { ...m, status: "delivered", messageId: data.message_id }
              : m
          )
        );
        break;

      case "status_update":
        setMessages((prev) =>
          prev.map((m) =>
            m.messageId === data.message_id ? { ...m, status: data.status } : m
          )
        );
        break;

      case "typing_start":
        setAgentTyping(true);
        break;

      case "stream_start":
        // Reset queue and buffer for a fresh stream
        tokenQueue.current = [];
        streamBuffer.current = "";
        streamMsgId.current = data.message_id;
        streamEnded.current = false;
        setStreaming(true);
        setAgentTyping(false);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "",
            streaming: true,
            messageId: data.message_id,
            timestamp: new Date(),
          },
        ]);
        // Drain one token per tick. When the queue empties AND the stream has
        // ended, finalize the message and stop the interval.
        clearInterval(streamInterval.current);
        streamInterval.current = setInterval(() => {
          if (tokenQueue.current.length > 0) {
            const token = tokenQueue.current.shift();
            streamBuffer.current += token;
            const content = streamBuffer.current;
            const msgId = streamMsgId.current;
            setMessages((prev) =>
              prev.map((m) => (m.messageId === msgId ? { ...m, content } : m))
            );
          } else if (streamEnded.current) {
            clearInterval(streamInterval.current);
            streamInterval.current = null;
            const msgId = streamMsgId.current;
            setMessages((prev) =>
              prev.map((m) => (m.messageId === msgId ? { ...m, streaming: false } : m))
            );
            setStreaming(false);
          }
        }, TOKEN_DRAIN_MS);
        break;

      case "stream_token":
        // Enqueue — the interval above will render it one per tick
        tokenQueue.current.push(data.token);
        break;

      case "stream_end":
        // Signal the drain interval to finalize once the queue empties
        streamEnded.current = true;
        break;

      case "reply":
        setAgentTyping(false);
        setAgentInfo({
          agentId: data.data?.agent_id,
          agentName: data.data?.agent_name,
          intent: data.data?.intent,
          needsEscalation: data.data?.needs_escalation,
        });

        // If this was a streamed reply, update with cleaned text + rich elements
        if (data.data?.streamed) {
          if (data.data?.rich_elements || data.data?.reply !== undefined) {
            setMessages((prev) =>
              prev.map((m) => {
                if (m.messageId !== data.message_id) return m;
                const updated = {
                  ...m,
                  content: data.data?.reply ?? m.content,
                  richElements: data.data?.rich_elements || null,
                  // Track C.10 / Batch 7: chat citations from
                  // ``chat_citations.resolve_citations()`` — { chunk_id, doc_id,
                  // status, filename?, page?, message? }; status ∈ {live,
                  // soft_deleted, hard_deleted}; the UI renders accordingly.
                  citations: data.data?.citations || null,
                  // Tier-2 #2: tool calls this turn produced.
                  toolCalls: data.data?.tool_calls || null,
                  agentName: data.data?.agent_name,
                  intent: data.data?.intent,
                };
                // Batch B1: fact-checker verdict {verdict, confidence, message_id},
                // rendered as a confidence pill via <VerificationBadge/>. Assigned
                // as a statement (not an object-literal property) so line coverage
                // credits it — asserted by useWebSocketChat.verification.test.jsx.
                updated.verification = data.data?.verification || null;
                return updated;
              })
            );
          }
          break;
        }

        // Non-streamed reply — add to messages
        const assistantMsg = {
          role: "assistant",
          content: data.data?.reply || "",
          richElements: data.data?.rich_elements || null,
          citations: data.data?.citations || null,
          toolCalls: data.data?.tool_calls || null,
          intent: data.data?.intent,
          latencyMs: data.data?.latency_ms,
          agentName: data.data?.agent_name,
          needsEscalation: data.data?.needs_escalation,
          isResolved: data.data?.is_resolved,
          conversationId: data.data?.conversation_id,
          timestamp: new Date(),
        };
        // Batch B1: verification assigned as a statement (see streamed path) so
        // line coverage credits it — asserted by the hook's verification test.
        assistantMsg.verification = data.data?.verification || null;
        setMessages((prev) => [...prev, assistantMsg]);
        break;

      case "escalation":
        setMessages((prev) => [
          ...prev,
          {
            role: "system",
            content: data.data?.message || "Escalation triggered",
            type: "escalation",
            timestamp: new Date(),
          },
        ]);
        break;

      case "user_typing":
        // Another user in the tenant is typing (for admin view)
        break;

      case "session_cleared":
        setMessages([]);
        setAgentInfo(null);
        break;

      case "error":
        setAgentTyping(false);
        setStreaming(false);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.message || "Unable to get a response. Please try again or rephrase your question.",
            error: true,
            timestamp: new Date(),
          },
        ]);
        break;

      default:
        log.warn("Unknown message type:", data.type);
    }
  }, []);

  // ─── Send Methods ────────────────────────────────────

  const sendMessage = useCallback((text, agentId = null) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      log.warn("Not connected");
      return;
    }

    // Add user message to UI immediately with "sent" status
    setMessages((prev) => [...prev, { role: "user", content: text, status: "sent", timestamp: new Date() }]);

    wsRef.current.send(JSON.stringify({
      type: "message",
      text,
      agent_id: agentId,
    }));
  }, []);

  const sendMessageWithFiles = useCallback((text, fileIds, agentId = null) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      log.warn("Not connected");
      return;
    }

    setMessages((prev) => [...prev, { role: "user", content: text || "(attached files)", status: "sent", fileIds, timestamp: new Date() }]);

    wsRef.current.send(JSON.stringify({
      type: "message_with_files",
      text: text || "Please review the attached files.",
      file_ids: fileIds,
      agent_id: agentId,
    }));
  }, []);

  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing" }));
    }
  }, []);

  const clearSession = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "clear" }));
    }
    setMessages([]);
    // Also wipe persisted history — clearSession is the explicit
    // "start over" action; without this localStorage would re-hydrate
    // the same messages on the next open.
    if (storageKey) {
      try { localStorage.removeItem(storageKey); } catch {}
    }
    setAgentInfo(null);
  }, [storageKey]);

  // ─── Lifecycle ───────────────────────────────────────

  useEffect(() => {
    connect();
    return () => {
      intentionalClose.current = true;
      clearTimeout(reconnectTimer.current);
      clearInterval(heartbeatTimer.current);
      clearInterval(streamInterval.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connect]);

  // Reconnect when auth changes
  useEffect(() => {
    const handleAuthChange = () => {
      if (wsRef.current) wsRef.current.close();
      setTimeout(connect, 500);
    };
    window.addEventListener("auth:login", handleAuthChange);
    return () => window.removeEventListener("auth:login", handleAuthChange);
  }, [connect]);

  return {
    messages,
    connected,
    reconnecting,
    streaming,
    agentTyping,
    agentInfo,
    sendMessage,
    sendMessageWithFiles,
    sendTyping,
    clearSession,
    reconnect: connect,
  };
}
